import { DBSchema, StoreNames, StoreKey, StoreValue } from 'idb';

export type InMemoryRow<
  Schema extends DBSchema,
  TableName extends StoreNames<Schema>,
> = StoreValue<Schema, TableName> & {
  id: StoreKey<Schema, TableName>;
  timestamp: number;
};

/**
 * In-memory реализация хранилища, повторяющая минимальную поверхность IndexedDB,
 * которую используют кэш-сервисы. Применяется как fallback, когда `indexedDB`
 * недоступен: данные живут в памяти и пропадают при перезагрузке страницы.
 * Хранилище ведёт данные по имени таблицы (`Map<tableName, Map<key, value>>`).
 */
export class InMemoryIdbService<
  Schema extends DBSchema,
  TableName extends StoreNames<Schema>,
> {
  private readonly tables = new Map<
    string,
    Map<StoreKey<Schema, TableName>, InMemoryRow<Schema, TableName>>
  >();

  private table(
    name: string,
  ): Map<StoreKey<Schema, TableName>, InMemoryRow<Schema, TableName>> {
    let table = this.tables.get(name);
    if (!table) {
      table = new Map();
      this.tables.set(name, table);
    }
    return table;
  }

  get(
    name: string,
    key: StoreKey<Schema, TableName>,
  ): InMemoryRow<Schema, TableName> | undefined {
    return this.table(name).get(key);
  }

  getAll(name: string): InMemoryRow<Schema, TableName>[] {
    return Array.from(this.table(name).values());
  }

  put(
    name: string,
    value: InMemoryRow<Schema, TableName>,
  ): StoreKey<Schema, TableName> {
    const key = value.id;
    this.table(name).set(key, value);
    return key;
  }

  delete(name: string, key: StoreKey<Schema, TableName>): void {
    this.table(name).delete(key);
  }

  clear(name: string): void {
    this.tables.delete(name);
  }

  hasExpired(name: string, threshold: number): boolean {
    return Array.from(this.table(name).values()).some(
      (row) => row.timestamp <= threshold,
    );
  }
}
